import axios from "axios"
import { API_URL } from "../utils/ApiLinks"
import type { ConversationDto, NewConversationDto, MarkAsReadResultDto } from "../types/message"

const getAuthHeaders = (contentType = "application/json") => ({
  "Content-Type": contentType,
  Authorization: `Bearer ${localStorage.getItem("authToken")}`,
})

export const debugApiEndpoints = async (profileId: number): Promise<void> => {
  const endpoints = [
    `/Contact/get-user-contacts/${profileId}`,
    `/Contact/conversations`,
    `/Messages/get-conversations/${profileId}`,
  ]

  for (const endpoint of endpoints) {
    try {
      const response = await axios.get(`${API_URL}${endpoint}`, {
        headers: getAuthHeaders(),
        timeout: 5000,
      })
      // Usar la variable response para eliminar la advertencia
      console.debug(`Endpoint ${endpoint} status:`, response.status);
    } catch (error: any) {}
  }
}

export const debugConversations = (conversations: ConversationDto[]): void => {
  const byType: Record<string, number> = {}
  conversations.forEach((conv) => {
    byType[conv.conversationType] = (byType[conv.conversationType] || 0) + 1
  })

  const participantCounts: Record<number, number> = {}
  conversations.forEach((conv) => {
    conv.participants.forEach((p) => {
      participantCounts[p.profileId] = (participantCounts[p.profileId] || 0) + 1
    })
  })

  const withMessages = conversations.filter((c) => c.messages && c.messages.length > 0).length
  const withoutMessages = conversations.length - withMessages
  const withProperty = conversations.filter((c) => c.propertyInfo).length
  
  // Usar las variables para eliminar las advertencias
  console.debug('Conversations stats:', {
    total: conversations.length,
    withMessages,
    withoutMessages,
    withProperty
  });
}

export const getConversationsREST = async (profileId: number): Promise<ConversationDto[] | undefined> => {
  try {
    const url = `${API_URL}/Contact/conversations`
    const response = await axios.get(url, {
      headers: getAuthHeaders(),
      params: { profileId },
    })

    if (response.data) {
      const conversations = Array.isArray(response.data) ? response.data : [response.data]
      debugConversations(conversations)
      return conversations
    }

    return []
  } catch (error: any) {
    try {
      const fallbackUrl = `${API_URL}/Contact/get-user-contacts/${profileId}`
      const response = await axios.get(fallbackUrl, { headers: getAuthHeaders() })

      if (response.data) {
        const conversations = Array.isArray(response.data) ? response.data : [response.data]
        debugConversations(conversations)
        return conversations
      }

      try {
        const thirdUrl = `${API_URL}/Messages/get-conversations/${profileId}`
        const response = await axios.get(thirdUrl, { headers: getAuthHeaders() })

        if (response.data) {
          const conversations = Array.isArray(response.data) ? response.data : [response.data]
          debugConversations(conversations)
          return conversations
        }

        return []
      } catch (thirdError) {
        return []
      }
    } catch (fallbackError) {
      return []
    }
  }
}

export const createConversationREST = async (
  conversationData: NewConversationDto,
): Promise<ConversationDto | undefined> => {
  try {
    const response = await axios.post(`${API_URL}/Contact/create-conversation`, conversationData, {
      headers: getAuthHeaders(),
    })
    return response.data
  } catch (error: any) {
    handleAuthError(error)
    return undefined
  }
}

export const markMessageAsReadREST = async (
  messageId: number,
  profileId: number,
): Promise<MarkAsReadResultDto | undefined> => {
  try {
    const response = await axios.post(
      `${API_URL}/Contact/mark-as-read`,
      { messageId, profileId },
      { headers: getAuthHeaders() },
    )
    return response.data
  } catch (error: any) {
    handleAuthError(error)
    return undefined
  }
}

export const markMessagesAsReadREST = async (conversationId: number, profileId: number): Promise<boolean> => {
  try {
    await axios.post(
      `${API_URL}/Contact/mark-all-as-read`,
      { conversationId, profileId },
      { headers: getAuthHeaders() },
    )
    return true
  } catch (error: any) {
    handleAuthError(error)
    return false
  }
}

export const isProfileInConversation = async (profileId: number, conversationId: number): Promise<boolean> => {
  try {
    const response = await axios.get(`${API_URL}/Contact/is-profile-in-conversation/${profileId}/${conversationId}`, {
      headers: getAuthHeaders(),
    })
    return response.data
  } catch (error: any) {
    handleAuthError(error)
    return false
  }
}

const handleAuthError = (error: any) => {
  if (error.response) {
    throw new Error(error.response.data?.message || error.message || "Error en la respuesta del servidor")
  } else if (error.request) {
    throw new Error("No se recibió respuesta del servidor. Verifica tu conexión a internet.")
  } else {
    throw new Error(error.message || "Ocurrió un error desconocido")
  }
}